// Visibility-based dwell tracker, replicating DocSend's model
// (research/notes_links.md §11): the timer runs only while the tab is visible,
// pauses on hidden, no idle detection. Flushes every 15s + on hide; sendBeacon
// carries the residual buffer on pagehide.
//
// Delivery is exactly-once: each flush freezes the accumulated buffer into a
// {seq, deltas} batch. A failed/ambiguous send retries the SAME batch with the
// SAME seq; the server's `lastSeq < seq` condition makes re-application a no-op,
// so seconds can never double-count. New ticks accumulate in a fresh buffer
// while a batch is pending.

const FLUSH_INTERVAL_MS = 15_000;
const RESUME_WINDOW_MS = 30 * 60 * 1000;

interface VisitRef {
  visitId: string;
  startedAt: string;
}

interface Batch {
  seq: number;
  deltas: { k: string; s: number }[];
}

const visitKey = (slug: string) => `ds_visit_${slug}`;

export function loadResumableVisit(slug: string): VisitRef | null {
  try {
    const raw = sessionStorage.getItem(visitKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VisitRef & { lastFlushAt: number };
    if (Date.now() - parsed.lastFlushAt > RESUME_WINDOW_MS) return null;
    return { visitId: parsed.visitId, startedAt: parsed.startedAt };
  } catch {
    return null;
  }
}

export function saveVisit(slug: string, ref: VisitRef): void {
  sessionStorage.setItem(
    visitKey(slug),
    JSON.stringify({ ...ref, lastFlushAt: Date.now() }),
  );
}

export class DwellTracker {
  private buffer = new Map<string, number>();
  private pending: Batch | null = null;
  private seq = 0;
  private currentKey: string | null = null;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private flushing = false;
  private stopped = false;

  constructor(
    private slug: string,
    private visit: VisitRef,
    private onUnavailable: () => void,
  ) {}

  /** Set the page currently occupying the viewport: "{docId}#v{NNN}#p{NNNN}" or null. */
  setCurrentPage(key: string | null): void {
    this.currentKey = key;
  }

  start(): void {
    this.tickTimer = setInterval(() => {
      if (this.stopped) return;
      if (document.visibilityState !== 'visible') return;
      if (!this.currentKey) return;
      this.buffer.set(this.currentKey, (this.buffer.get(this.currentKey) ?? 0) + 1);
    }, 1000);

    this.flushTimer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);

    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pagehide', this.onPageHide);
  }

  stop(): void {
    this.stopped = true;
    if (this.tickTimer) clearInterval(this.tickTimer);
    if (this.flushTimer) clearInterval(this.flushTimer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pagehide', this.onPageHide);
  }

  private onVisibility = () => {
    if (document.visibilityState === 'hidden') void this.flush();
  };

  private onPageHide = () => {
    this.beacon();
  };

  /** Freeze the buffer into the pending batch (or reuse the pending one). */
  private nextBatch(): Batch | null {
    if (this.pending) return this.pending;
    if (!this.buffer.size) return null;
    const deltas = [...this.buffer.entries()]
      .map(([k, s]) => ({ k, s: Math.min(s, 120) }))
      .filter((d) => d.s > 0);
    this.buffer.clear();
    if (!deltas.length) return null;
    this.seq += 1;
    this.pending = { seq: this.seq, deltas };
    return this.pending;
  }

  private body(batch: Batch, ended: boolean): string {
    return JSON.stringify({
      visitId: this.visit.visitId,
      startedAt: this.visit.startedAt,
      seq: batch.seq,
      ended,
      deltas: batch.deltas,
    });
  }

  async flush(): Promise<void> {
    if (this.flushing) return;
    const batch = this.nextBatch();
    if (!batch) return;
    this.flushing = true;
    try {
      const res = await fetch(`/api/view/${this.slug}/beat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: this.body(batch, false),
        keepalive: true,
      });
      if (res.ok) {
        const data = (await res.json()) as { ok: boolean; reason?: string };
        if (!data.ok && data.reason === 'unavailable') {
          this.stop();
          this.onUnavailable();
          return;
        }
        // Applied (or deduped as already-applied): either way it's durable.
        this.pending = null;
        saveVisit(this.slug, this.visit);
      }
      // Non-2xx: keep the batch; the next flush retries the same seq.
    } catch {
      // Network error: batch stays pending for retry.
    } finally {
      this.flushing = false;
    }
  }

  /** Final best-effort delivery on pagehide (pending batch + residual buffer
   *  merged under the pending seq — safe because a beacon after a lost ack can
   *  only add the residual seconds it legitimately accrued). */
  private beacon(): void {
    let batch = this.pending;
    if (batch && this.buffer.size) {
      for (const [k, s] of this.buffer) {
        const existing = batch.deltas.find((d) => d.k === k);
        if (existing) existing.s = Math.min(existing.s + s, 120);
        else batch.deltas.push({ k, s: Math.min(s, 120) });
      }
      this.buffer.clear();
    }
    if (!batch) batch = this.nextBatch();
    if (!batch) return;
    navigator.sendBeacon(
      `/api/view/${this.slug}/beat`,
      new Blob([this.body(batch, true)], { type: 'application/json' }),
    );
    this.pending = null;
  }
}
